import { TItem, TItemType, TResolvedItemsMap } from '@modules/items';
import { IDetectedInlineBlock, IInlineBlockDetectionRun } from './inlineBlocks.types';
import { IDetectedReference, IDetectedSelfReference, IReferenceDetectionRun } from './references.types';

export enum EDetectionType {
  REFERENCE = 'REFERENCE',
  INLINE_BLOCK = 'INLINE_BLOCK',
}

export enum EDetectionStatus {
  CREATED = 'CREATED',
  ANALYZED = 'ANALYZED',
  INSERTING = 'INSERTING',
  COMPLETED = 'COMPLETED',
  INSERTION_FAILED = 'INSERTION_FAILED',
  ANALYSIS_FAILED = 'ANALYSIS_FAILED',
  CANCELLED = 'CANCELLED',
}

export enum EDetectionPollingStatus {
  PENDING = 'PENDING',
  READY = 'READY',
}

type TSourceItemType = 'DOCUMENT';

/**
 * Generic "item in scope" descriptor. Specialize via the TKind union.
 * Example: ISelectedItem<'DOCUMENT' | 'FILE'>
 */
export interface IItemDefinition<TKind extends TItemType> {
  item: string;
  itemType: TKind;
}

export interface IItemDefinitionDto<TKind extends TItemType> {
  itemId: string;
  itemType: TKind;
}

/**
 * Common fields for a detected match inside HTML content.
 * Keep this minimal so both modules can extend without conflicts.
 */
export interface IBaseDetectedElement {
  /** Unique id of the occurrence (also usable as DOM data-id) */
  id: string;

  sourceItem: string;
  sourceItemType: TSourceItemType;

  /** Absolute offsets within the HTML blob */
  contentStart: number;
  contentEnd: number;

  /** Literal matched text and previews */
  snippet: string;
  htmlSnippet: string;
  contextSnippet: string;

  /** Optional anchor element id for deep-linking/scroll */
  elementId?: string;

  /** List of warning messages generated during detection (e.g., snippet in word context) */
  warnings?: string[];

  /** Whether has been inserted into the target document */
  inserted: boolean;
}

/**
 * Minimal shared run skeleton. Modules extend this with their specifics.
 * Note: Only properties common to both flows are placed here.
 */
export interface IBaseDetectionRun {
  _id: string;
  active: boolean;

  organization: string;
  creator: string;

  rootItem: string;

  status: EDetectionStatus;
  errorMessage?: string;

  /** ISO timestamp when created */
  createdDate: string;
  /** ISO timestamp when the run completed */
  completedDate?: string;
  /** ISO timestamp when the run was deactivated/cancelled */
  deactivatedDate?: string;

  warnings?: string[];
}

/**
 * Standardized return/polling helpers (generic to avoid importing module-specific maps).
 */
export type TDetectionReturn<TRun extends IBaseDetectionRun> = {
  run: TRun;
  resolvedItems: TResolvedItemsMap;
};

export type TPollDetectionReturn<TRun extends IBaseDetectionRun> =
  | { status: EDetectionPollingStatus.PENDING }
  | {
      status: EDetectionPollingStatus.READY;
      run: TRun;
      resolvedItems: TResolvedItemsMap;
      /** ISO timestamp supplied by the backend after which the run may no longer be submitted. */
      targetDate: string;
    };

/**
 * Create-run result: identical contract across modules.
 */
export type TCreateDetectionReturn =
  | { runId: string; lockedUserIds?: undefined }
  | { runId?: undefined; lockedUserIds: string[] };

/**
 * View modes for the detection UI.
 */
export enum EDetectionViewMode {
  INITIAL = 'INITIAL',
  PREVIEW = 'PREVIEW',
  SUMMARY = 'SUMMARY',
}

// --- preview table types ---
export type TDetectionsRowItem = IDetectedReference | IDetectedSelfReference | IDetectedInlineBlock;
export type TDetectionsRowVariant = 'references' | 'inline-blocks';
